import { AppColors } from "@/styles/colors";
import { Button, Flex, Grid, GridItem, HStack, Text } from "@chakra-ui/react";
import { Check, Info, X } from "lucide-react";

import "@/components/common/section-components/card/text-elements.css";
import { Dispatch, SetStateAction } from "react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { SlotRequestStatus } from "@/constants/entity-status";
import { SlotRequestDetail } from "@/pages/grain-requests/slot-requests/schema/slot-requests.schema";

interface Props {
  slotRequest?: SlotRequestDetail | null;
  loadingSlotRequest?: boolean;
  loadingAssignSlots?: boolean;
  loadingRejectSlotRequest?: boolean;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  setOpenInfoModal: (open: boolean) => void;
  setOpenAssignSlots: Dispatch<SetStateAction<boolean>>;
  loadingSendInfo: boolean;
}

export const SlotRequestAmountAndActions = ({
  slotRequest,
  loadingSlotRequest,
  loadingAssignSlots,
  loadingRejectSlotRequest,
  setOpenRejectModal,
  setOpenInfoModal,
  setOpenAssignSlots,
  loadingSendInfo,
}: Props) => {
  const { userPermissions } = useRoles();

  const cantOperateSlotRequest =
    slotRequest?.estado == SlotRequestStatus.FINISHED ||
    slotRequest?.estado == SlotRequestStatus.REJECTED;

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.SLOT_REQUESTS && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex justifyContent="space-between" flexWrap={"wrap"}>
      <Grid templateColumns="repeat(3, auto)" gap="36px">
        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              textTransform="uppercase"
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
            >
              A planta
            </Text>
            <Flex alignItems="center" justifyContent="space-between" gap="6px">
              <Flex alignItems="center" gap="8px">
                <Text
                  fontSize={{
                    base: "15px",
                    sm: "15px",
                    md: "15px",
                    lg: "18px",
                    xl: "20px",
                  }}
                  fontWeight={700}
                >
                  {`${slotRequest?.cuposAPlanta} cupos`}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </GridItem>
        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
              textTransform={"uppercase"}
            >
              Directos
            </Text>
            <Flex alignItems="flex-start" gap="6px">
              <Flex alignItems="center" gap="8px">
                <Text
                  fontSize={{
                    base: "15px",
                    sm: "15px",
                    md: "15px",
                    lg: "18px",
                    xl: "20px",
                  }}
                  fontWeight={700}
                >
                  {`${slotRequest?.cuposDirectos} cupos`}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
      {hasWriteAccess && !cantOperateSlotRequest && (
        <HStack
          ml="auto"
          mt={{ base: 4, md: 0 }}
          paddingTop="10px"
          css={{
            "@media (max-width: 1350px) and (min-width: 1272px)": {
              "& button": {
                paddingInline: "4px",
                paddingBlock: "2px",
              },
              "& button svg": {
                width: "14px",
                height: "14px",
              },
            },
            "@media (max-width: 1590px) and (min-width: 1351px)": {
              "& button": {
                paddingInline: "10px",
                paddingBlock: "4px",
              },
              "& button svg": {
                width: "16px",
                height: "16px",
              },
            },
            "@media (min-width: 1591px)": {
              "& button": {
                paddingInline: "12px",
                paddingBlock: "6px",
              },
              "& button svg": {
                width: "18px",
                height: "18px",
              },
            },
          }}
        >
          <Button
            disabled={
              loadingSlotRequest ||
              loadingRejectSlotRequest ||
              loadingAssignSlots ||
              cantOperateSlotRequest ||
              slotRequest?.estado === SlotRequestStatus.SLOTS_ASSIGNED
            }
            bgColor={AppColors.LIGHT_GREEN}
            onClick={() => setOpenAssignSlots(true)}
            loading={loadingAssignSlots}
            data-testid="slot-request-execute-btn"
          >
            <Check />
            <Text
              fontWeight={600}
              css={{
                "@media (max-width: 1350px) and (min-width: 1272px)": {
                  fontSize: "10px",
                },
                "@media (max-width: 1590px) and (min-width: 1351px)": {
                  fontSize: "12px",
                },
                "@media (min-width: 1591px)": {
                  fontSize: "16px",
                },
              }}
            >
              Asignar cupos
            </Text>
          </Button>

          {slotRequest?.estado !== SlotRequestStatus.SLOTS_ASSIGNED && (
            <Button
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.BLACK_TEXT_SECONDARY}
              disabled={
                loadingSlotRequest ||
                loadingRejectSlotRequest ||
                loadingSendInfo ||
                Number(slotRequest?.mensajes?.length) >= 5 ||
                slotRequest?.estado === SlotRequestStatus.REJECTED ||
                slotRequest?.estado === SlotRequestStatus.FINISHED
              }
              onClick={() => setOpenInfoModal(true)}
            >
              <Info />
              <Text
                fontWeight={600}
                css={{
                  "@media (max-width: 1350px) and (min-width: 1272px)": {
                    fontSize: "10px",
                  },
                  "@media (max-width: 1590px) and (min-width: 1351px)": {
                    fontSize: "12px",
                  },
                  "@media (min-width: 1591px)": {
                    fontSize: "16px",
                  },
                }}
              >
                Requerir información
              </Text>
            </Button>
          )}

          <Button
            disabled={
              loadingSlotRequest ||
              loadingRejectSlotRequest ||
              loadingAssignSlots ||
              cantOperateSlotRequest
            }
            bgColor={AppColors.RED_ICON}
            onClick={() => setOpenRejectModal(true)}
            data-testid="slot-request-reject-btn"
          >
            <X />
            <Text
              fontWeight={600}
              css={{
                "@media (max-width: 1350px) and (min-width: 1272px)": {
                  fontSize: "10px",
                },
                "@media (max-width: 1590px) and (min-width: 1351px)": {
                  fontSize: "12px",
                },
                "@media (min-width: 1591px)": {
                  fontSize: "16px",
                },
              }}
            >
              Rechazar
            </Text>
          </Button>
        </HStack>
      )}
    </Flex>
  );
};
